"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = ["ALL", "METHODOLOGY", "ASSESSMENT", "PEDAGOGY", "RESEARCH", "UPDATES"];
const pullQuotes = [{
  id: "pq-1",
  quote: "Most marks are lost not in the absence of knowledge, but in the mechanics of the process.",
  attribution: "Resseti Research Lead",
  insertAfter: 3
}, {
  id: "pq-2",
  quote: "Diagnostic precision is the difference between guessing and knowing exactly where a student falters.",
  attribution: "Setura Engineering Team",
  insertAfter: 7
}];
function formatDate(dateString?: string): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function JournalGrid() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 border border-border rounded-lg p-6">
            <div className="h-3 w-24 animate-pulse bg-muted/30" />
            <div className="h-6 w-3/4 animate-pulse bg-muted/30" />
            <div className="h-4 w-full animate-pulse bg-muted/30" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center gap-4 py-16 text-center">
        <p className="font-mono text-sm tracking-widest text-muted-foreground">
          UNABLE TO LOAD INSIGHTS
        </p>
        <Button onClick={() => refetch()} variant="outline">
          Retry
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-16 text-center">
        <p className="font-mono text-sm tracking-widest text-muted-foreground">
          NO INSIGHTS PUBLISHED YET
        </p>
      </div>;
  }
  const items: Array<{
    type: "post";
    post: (typeof posts)[number];
    index: number;
    featured: boolean;
  } | {
    type: "quote";
    quote: (typeof pullQuotes)[number];
  }> = [];
  posts.forEach((post, i) => {
    items.push({
      type: "post",
      post,
      index: i,
      featured: i === 0
    });
    const quote = pullQuotes.find(q => q.insertAfter === i);
    if (quote) {
      items.push({
        type: "quote",
        quote
      });
    }
  });
  return <>
      <div className="columns-1 gap-8 md:columns-2 lg:columns-3 [&>*]:mb-8 [&>*]:break-inside-avoid">
        {items.map((item, idx) => {
        if (item.type === "quote") {
          return <figure key={item.quote.id} data-index={idx} className="group bg-secondary/40 p-10 md:p-12 rounded-lg shadow-md">
                <blockquote className="font-serif text-2xl italic leading-snug text-foreground md:text-3xl">
                  &ldquo;{item.quote.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-8 font-mono text-xs tracking-widest text-[hsl(39_100%_80%)]">
                  {item.quote.attribution.toUpperCase()}
                </figcaption>
              </figure>;
        }
        const {
          post,
          featured
        } = item;
        const excerpt = stripHtml(post.excerpt);
        const authorName = post.author?.name || "Resseti Research";
        const authorAvatar = post.author?.avatar?.["48"];
        return <article key={post.id} data-index={idx} className={`group flex flex-col border border-border rounded-lg p-6 md:p-7 transition duration-500 ease-out hover:-translate-y-1 hover:border-[hsl(39_100%_80%)]/50 ${featured ? "lg:!break-inside-avoid" : ""}`}>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3 font-mono text-[11px] tracking-widest">
                  <span className="text-[hsl(39_100%_80%)]">
                    {(post.categories?.[0]?.name || "INSIGHT").toUpperCase()}
                  </span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground">
                    {formatDate(post.date)}
                  </span>
                </div>

                <Link to={post.link || "#"} className="block">
                  <h3 className={`font-default font-medium leading-tight text-foreground ${featured ? "text-3xl md:text-4xl" : "text-xl md:text-[22px]"}`}>
                    <span className="bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_1px] bg-[position:0_95%] bg-no-repeat transition-[background-size] duration-500 ease-out group-hover:bg-[length:100%_1px] group-hover:text-[hsl(39_100%_80%)]">
                      {post.title}
                    </span>
                  </h3>
                </Link>

                {excerpt && <p className="line-clamp-2 text-[15px] leading-relaxed text-muted-foreground">
                    {excerpt}
                  </p>}

                <div className="mt-2 flex items-center gap-3 border-t border-border pt-4">
                  <SafeHydrate><Avatar className="size-7 rounded-lg">
                    {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
                    <AvatarFallback className="bg-secondary text-[10px] font-mono text-secondary-foreground">
                      {authorName.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <span className="font-mono text-[11px] tracking-widest text-muted-foreground">
                    {authorName.toUpperCase()}
                  </span>
                </div>
              </div>
            </article>;
      })}
      </div>

      <div className="mt-20 flex flex-col items-center gap-6">
        {hasNext && <Button onClick={async () => await loadMore()} disabled={isRefetching} size="lg" className="rounded-lg px-10 py-6 font-mono text-xs tracking-[0.2em] bg-[hsl(39_100%_80%)] text-[hsl(254_70%_5%)] hover:bg-white">
            {isRefetching ? "LOADING..." : "LOAD MORE INSIGHTS"}
          </Button>}
      </div>
    </>;
}
export default function DiagnosticInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4876" id="diagnostic-insights" className="bg-background text-foreground px-6 pb-40 pt-48 md:px-10 md:pb-40 md:pt-52">
      <div className="mx-auto max-w-[1400px]">
        <header className="flex flex-col items-center text-center">
          <p className="font-mono text-xs tracking-[0.25em] text-[hsl(39_100%_80%)]">
            RESSETI RESEARCH · EST. 2020
          </p>

          <h1 className="mt-10 font-default leading-[0.95] tracking-tight text-foreground">
            <span style={{
            fontSize: "clamp(64px, 12vw, 200px)"
          }}>
              Diagnostic <span className="italic">Insights</span>.
            </span>
          </h1>

          <p className="mt-12 max-w-[580px] text-[17px] leading-relaxed text-muted-foreground md:text-[19px]">
            Technical analysis of student performance. Mapping process errors to mark-scheme logic.
          </p>
        </header>

        <div className="mt-24 flex flex-col gap-6 border-y border-border py-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            {categories.map((cat, i) => <Button key={cat} data-index={i} variant={i === 0 ? "default" : "outline"} size="sm" className={`rounded-lg px-4 py-2 font-mono text-[10px] tracking-[0.2em] md:text-[11px] ${i === 0 ? "bg-[hsl(39_100%_80%)] text-[hsl(254_70%_5%)] hover:bg-white" : "border-border bg-transparent text-foreground/70 hover:border-[hsl(39_100%_80%)] hover:bg-transparent hover:text-[hsl(39_100%_80%)]"}`}>
                {cat}
              </Button>)}
          </div>

          <div className="relative w-full md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(39_100%_80%)]" />
            <Input type="search" placeholder="Search insights..." className="h-10 rounded-lg border-border bg-transparent pl-10 font-mono text-xs tracking-wider placeholder:text-muted-foreground/70" />
          </div>
        </div>

        <div className="mt-20">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 9,
          order: "desc",
          orderby: "date",
          status: ["publish"]
        }}>
            <JournalGrid />
          </PostsProvider>
        </div>
      </div>
    </section>;
}